<template>
  <div
    class="decimal-number"
    :style="
      `border-color: ${
        isInputOnFocus ? focusColor : borderColor !== '' ? borderColor : ''
      }`
    "
    :class="[{ onFocus: isInputOnFocus }, { disabled: disabled }]"
    @click="onLabelFocus"
    tabindex="11000"
  >
    <span @click="onLabelFocus" @focus="onLabelFocus" tabindex="-1">{{
      formattedValue
    }}</span>
    <label
      v-if="lock === true"
      class="mbe-textfield-label"
      :class="{ 'shipment-sidebar-locked': lock }"
    >
      <i class="el-icon-lock"></i>
    </label>
    <input
      type="text"
      :value="value"
      :name="name"
      :tabindex="disabled ? -1 : 0"
      :disabled="disabled"
      ref="inputDecimal"
      @focus="onInputFocus"
      @blur="onInputBlur"
      @input="onInput"
    />
  </div>
</template>

<script>
import * as numbro from "numbro";
import { isUk } from "@/services/Helpers";

export default {
  props: {
    value: {
      default: 0
    },
    decimals: {
      default: false
    },
    focusColor: {
      default: "inherit"
    },
    borderColor: {
      default: ""
    },
    min: {
      default: 0
    },
    max: {
      default: 0
    },
    disabled: {
      default: false
    },
    name: {
      default: ""
    },
    emptyIfZero: {
      default: false
    },
    nullIfNaN: {
      default: false
    },
    lock: {
      default: false
    }
  },
  data() {
    return {
      isInputOnFocus: false,
      oldValue: JSON.parse(JSON.stringify(this.value))
    };
  },
  methods: {
    formatValue(event, value) {
      if (this.disabled) return;
      // replaces ',' for '.'
      value = value.replace(",", ".");
      // removes non-digits characters or '.'
      value = value.replace(/[^\d.-]/g, "");
      // if min 0, convert negative numbers to positive
      if (this.min >= 0) value = value.replace("-", "");
      // if max
      if (this.max > 0 && value > this.max) value = this.max.toString();
      // if more than 1 zero.
      if (value.split(".").length > 2) {
        value = parseFloat(value).toFixed(this.decimals);
      }
      // if '.' on position 0
      if (value === ".") value = "0.";
      // updates input value
      if (event) event.target.value = value;

      // converts input value to javascript number
      value = numbro(parseFloat(value)).format({ mantissa: this.decimals });
      value = numbro.unformat(value);
      if (isNaN(value) || value == null) {
        if (this.nullIfNaN) {
          value = null;
        } else {
          value = 0;
        }
      }

      // update parent v-model
      return value;
    },
    onInputFocus(e) {
      if (this.disabled) return;
      e.target.setSelectionRange(0, 9999);
      this.isInputOnFocus = true;

      this.oldValue = this.value;
    },
    onLabelFocus() {
      if (this.disabled) return;
      this.$refs.inputDecimal.focus();
    },
    onInputBlur(event) {
      if (this.disabled) return;
      this.isInputOnFocus = false;
      let value = null;
      const decimals = `1e${this.decimals}`;
      if (isNaN(this.value) || this.value == null) {
        if (this.nullIfNaN) {
          value = null;
        }
      } else {
        value = Math.round(this.value * decimals) / decimals;
      }
      this.$emit("input", value);
      // simulate on change only if we're using Microsoft Edge
      this.$emit("blur", event);
      const emitValue = this.formatValue(event, event.target.value);
      if (emitValue !== undefined) {
        this.$emit("change", emitValue);
      }

      if (value !== this.oldValue) {
        this.$emit("inputChanged", event);
      }
    },
    onInput(event) {
      const emitValue = this.formatValue(event, event.target.value);
      if (emitValue !== undefined) {
        this.$emit("input", emitValue);
      }
      this.$emit("inputEvent", event);
    }
  },
  computed: {
    formattedValue() {
      if (isUk()) {
        numbro.setLanguage("en-GB");
      } else {
        numbro.setLanguage("it-IT");
      }

      if (this.nullIfNaN && this.value == null) return null;
      if (this.emptyIfZero && this.value === 0) return "";
      if (isNaN(this.value) || this.value == null) return null;

      const num = numbro(this.value).format({
        thousandSeparated: true,
        mantissa: this.decimals
      });

      return num;
    }
  }
};
</script>

<style lang="postcss" scoped>
.decimal-number {
  position: relative;
  height: 100%;
  width: 100%;
  outline: 0;
  transition: border-color 0.15s ease-out;
}

.decimal-number:hover {
  cursor: text;
}

.decimal-number.onFocus input {
  z-index: 1;
  opacity: 1;
}

.decimal-number.onFocus span {
  z-index: -1;
  opacity: 0;
}

input {
  border: none;
  background: none;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  height: 100%;
  width: 100%;
  opacity: 0;
  color: inherit;
  text-align: inherit;
  pointer-events: none;
}

input:focus {
  outline: 0;
}

span {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  color: inherit;
  text-align: inherit;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.table thead th {
  font-weight: 700;
}

.mbe-textfield-label {
  top: 0;
}
</style>
